/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import Plot from 'react-plotly.js';
import type { Layout } from 'plotly.js';
import type { StoredPlotlyResponse } from '@/types/eeCheck';

interface PlotlyFigureProps {
    figure: StoredPlotlyResponse;
    fillParent?: boolean;
    minWidth?: CSSProperties['minWidth'];
    revision?: number;
}

function getDarkModeLayout(layout: Partial<Layout>): Partial<Layout> {
    return {
        paper_bgcolor: 'rgba(0, 0, 0, 0)',
        plot_bgcolor: 'rgba(0, 0, 0, 0)',
        font: {
            ...layout.font,
            color: '#e8edf4',
        },
        hoverlabel: {
            ...layout.hoverlabel,
            bgcolor: '#111827',
            bordercolor: '#64748b',
            font: {
                ...layout.hoverlabel?.font,
                color: '#f8fafc',
            },
            namelength: -1,
        },
        xaxis: {
            ...layout.xaxis,
            gridcolor: 'rgba(148, 163, 184, 0.28)',
            linecolor: '#64748b',
            zerolinecolor: '#64748b',
        },
        yaxis: {
            ...layout.yaxis,
            gridcolor: 'rgba(148, 163, 184, 0.28)',
            linecolor: '#64748b',
            zerolinecolor: '#64748b',
        },
        ...(layout.yaxis2
            ? {
                yaxis2: {
                    ...layout.yaxis2,
                    gridcolor: 'rgba(148, 163, 184, 0.28)',
                    linecolor: '#64748b',
                    zerolinecolor: '#64748b',
                },
            }
            : {}),
    };
}

function PlotlyFigure({ figure, fillParent = false, minWidth, revision }: PlotlyFigureProps) {
    const shellRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState<{ width: number; height: number } | null>(null);
    const [isDarkTheme, setIsDarkTheme] = useState(() => (
        document.documentElement.getAttribute('data-theme') === 'dark'
    ));

    useEffect(() => {
        const shell = shellRef.current;
        if (!fillParent || !shell) {
            return;
        }

        // Window resize events do not cover grid resizing or the end of API loading.
        const observer = new ResizeObserver(([entry]) => {
            const width = Math.floor(entry.contentRect.width);
            const height = Math.floor(entry.contentRect.height);
            if (width > 0 && height > 0) {
                setSize((previous) => previous?.width === width && previous.height === height
                    ? previous
                    : { width, height });
            }
        });
        observer.observe(shell);
        return () => observer.disconnect();
    }, [fillParent]);

    useEffect(() => {
        const rootElement = document.documentElement;
        const updateTheme = () => {
            setIsDarkTheme(rootElement.getAttribute('data-theme') === 'dark');
        };
        const observer = new MutationObserver(updateTheme);

        updateTheme();
        observer.observe(rootElement, { attributes: true, attributeFilter: ['data-theme'] });

        return () => {
            observer.disconnect();
        };
    }, []);

    const layout = {
        autosize: true,
        ...(figure.layout ?? {}),
        ...(isDarkTheme ? getDarkModeLayout(figure.layout ?? {}) : {}),
        ...(fillParent && size ? { ...size, autosize: true } : {}),
    };

    return (
        <div
            className={`chart-surface overflow-hidden rounded-2xl border border-base-300 bg-base-100${fillParent ? ' chart-surface--fill' : ''}`}
            style={{
                width: '100%',
                height: fillParent ? '100%' : undefined,
                minWidth,
            }}
        >
            <div ref={shellRef} className={`plotly-chart-shell content-surface${fillParent ? ' plotly-chart-shell--fill' : ''}`}>
                <Plot
                    data={figure.data ?? []}
                    layout={layout}
                    config={{
                        responsive: true,
                        ...(figure.config ?? {}),
                    }}
                    className={`plotly-chart${fillParent ? ' plotly-chart--fill' : ''}`}
                    revision={revision}
                    useResizeHandler
                    style={{ width: '100%', height: '100%' }}
                />
            </div>
        </div>
    );
}

export default PlotlyFigure;
